/**
 * Created by jonas on 2022-01-11.
 */
import {StageLayer} from "@nolimitcity/slot-launcher/bin/StageLayer";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import {gsap} from "gsap";
import {ContinueButton} from "@nolimitcity/slot-launcher/bin/gui/buttons/concretebuttons/ContinueButton";
import {Outsourced} from "../Outsourced";
import {OutsourcedSound} from "../sound/OutsourcedSound";
import {OutsourcedGameAssets} from "../OutsourcedGameAssets";
import {BasedCountUp} from "../components/BasedCountUp";
import {CrashGame} from "../components/CrashGame";
import {SpineGSAPWrapper} from "../animation/SpineGSAPWrapper";
import {UpgradeBet} from "../components/UpgradeBet";

export class OutsourcedTransitionScreenView extends PIXI.Container implements StageLayer {
    protected button:ContinueButton;
    public introSpine:PIXI.spine.Spine;
    public maxWinSpine:PIXI.spine.Spine;
    public introDarkening:PIXI.Sprite;
    public totalWin:PIXI.Text;
    public countUp:BasedCountUp;
    crashGame:CrashGame;
    upgradeBet:UpgradeBet;
    buttonSound:string;

    private clickCallback?:Function;
    //private outroInformation:FreespinsOutroInformation;

    constructor(name?:string) {
        super();
        this.name = name ? name : "OutsourcedTransitionScreenView";
        const coloredSquare = (color:string) => {
            const square = new PIXI.Sprite(PIXI.Texture.WHITE);
            square.tint = PIXI.utils.string2hex(color);
            square.position.set(360, 360);
            square.anchor.set(0.5, 0.5);
            square.alpha = 0;
            //square.visible = false;
            return square;
        }
        this.introDarkening = coloredSquare("#000033")
        this.addChild(this.introDarkening);
        //this.outroInformation = new FreespinsOutroInformation();

        this.introSpine = new PIXI.spine.Spine(Outsourced.assetLoader.getSpineAsset(OutsourcedGameAssets.FS_INTRO));
        this.introSpine.position.set(360, 360);
        this.introSpine.alpha = 0;
        this.addChild(this.introSpine);

        this.countUp = new BasedCountUp();
        this.countUp.position.set(360, 240);
        this.countUp.visible = false;
        this.addChild(this.countUp);

        const spinsPlayedStyle = new PIXI.TextStyle({
            fontSize: 50,
            "dropShadow": true,
            "dropShadowAngle": 7.9,
            "dropShadowColor": "#000033",
            "fill": "#FFFFFF",
            "fontFamily": "Helvetica",
            "fontWeight": "bolder",
            "lineJoin": "round",
            "miterLimit": 0,
            "stroke": "#000033",
            "strokeThickness": 4
        });
        this.totalWin = new PIXI.Text(NolimitApplication.apiPlugin.translations.translate("Total Win"), spinsPlayedStyle);
        this.totalWin.position.set(360, 150);
        this.totalWin.anchor.set(0.5, 0.5);
        this.totalWin.visible = false;
        this.addChild(this.totalWin);

        this.crashGame = new CrashGame();
        this.addChild(this.crashGame);

        this.upgradeBet = new UpgradeBet();
        this.addChild(this.upgradeBet);

        this.maxWinSpine = new PIXI.spine.Spine(Outsourced.assetLoader.getSpineAsset(OutsourcedGameAssets.MAX_WIN_SPINE));
        this.maxWinSpine.position.set(360, 360);
        this.maxWinSpine.alpha = 0;
        this.addChild(this.maxWinSpine);

        this.button = new ContinueButton();
        this.button.addClickCallback(() => this.onButtonClick());
        this.button.position.set(360, 610);
        this.button.pivot.set(this.button.width * 0.5, this.button.height * 0.5);
        this.button.enable(false);
        this.button.alpha = 0;
        this.addChild(this.button);

        this.onResize();
    }

    onResize():void {
        if (NolimitApplication.isLandscape) {
            this.button.position.set(360, 610);
        } else {
            this.button.position.set(360, NolimitApplication.screenBounds.bottom - 250);
        }
        this.introDarkening.width = NolimitApplication.screenBounds.width;
        this.introDarkening.height = NolimitApplication.screenBounds.height;
        this.crashGame.onResize();

    }

    private onButtonClick() {
        this.button.enable(false);
        if (this.clickCallback == undefined) {
            throw new Error("No button click callback");
        }
        this.clickCallback();
        Outsourced.soundPlayer.playSound(this.buttonSound);
        gsap.to(this.button, {duration:0.2, alpha:0});
        delete this.clickCallback;
    }

    public enableButton(cb:Function, sound = OutsourcedSound.CONTINUE_BUTTON):gsap.core.Timeline {
        const tl:gsap.core.Timeline = gsap.timeline();
        tl.add(() => {
            this.buttonSound = sound;
            this.clickCallback = cb;
            this.button.enable(true);
        });
        tl.add(gsap.to(this.button, {duration:0.2, alpha:1}));
        return tl;
    }

    public showFreespinsIntro(fiveSpins:boolean):gsap.core.Timeline {
        return gsap.timeline()
            .add(() => {
                this.introDarkening.tint = PIXI.utils.string2hex(fiveSpins ? "#ff0033" : "#9900ff")
            })
            .add(gsap.to(this.introDarkening, {alpha: 0.7, duration: 0.25}))
            .add(() => {
                this.introSpine.state.setAnimation(0, `FS_Intro_${fiveSpins ? 5 : 3}_Loop`, true);
                this.introSpine.alpha = 1;
            }, '<');
    }

    public hideFreespinsIntro():gsap.core.Timeline {
        return gsap.timeline()
            .add(gsap.to(this.introDarkening, {alpha: 0, duration: 0.25}))
            .add(() => {
                this.introSpine.state.setEmptyAnimation(0, 0);
                this.introSpine.alpha = 0
            }, '<');
    }

    public showFreespinsOutro(fiveSpins:boolean):gsap.core.Timeline {
        const didntProfit = Outsourced.isWinBelowStake() || Outsourced.model.data.accumulatedRoundWin == 0;
        return gsap.timeline()
            .add(() => {
                this.introDarkening.tint = PIXI.utils.string2hex(fiveSpins ? "#ff0033" : "#9900ff")
            })
            .add(gsap.to(this.introDarkening, {alpha: 0.7, duration:0.25}))
            .add(() => {
                //this.countUp.show(0);
                this.totalWin.visible = !didntProfit;
                this.countUp.visible = true;
            }, '<')
            .add(didntProfit
                 ? this.countUp.countUpInstant(Outsourced.model.data.accumulatedRoundWin, 0)
                 : this.countUp.countUp(Outsourced.model.data.accumulatedRoundWin, 0, 2), '<')
            .add(() => {}, '>0.3');
    }

    public hideFreespinsOutro():gsap.core.Timeline {
        return gsap.timeline()
            .add(gsap.to(this.introDarkening, {alpha: 0, duration:0.25}))
            .add(() => {
                this.countUp.visible = false;
                this.totalWin.visible = false;
            }, '<');
    }

    public showMaxWin(maxierWin:boolean = false):gsap.core.Timeline {
        return gsap.timeline()
            .add(() => {
                this.introDarkening.tint = PIXI.utils.string2hex("#000033")
            })
            .add(gsap.to(this.introDarkening, {alpha: 0.7, duration: 0.25}))
            .add(SpineGSAPWrapper.createTimeline(this.maxWinSpine, 0, maxierWin ? "MW2_Intro" : Outsourced.isNJServer ? "wincap_NJ_US_50000x_Intro" : "MW_Intro"))
            .add(() => {
                this.maxWinSpine.alpha = 1;
            }, '<')
            .add(() => {
                this.maxWinSpine.state.setAnimation(0, maxierWin ? "MW2_Loop" : Outsourced.isNJServer ? "wincap_NJ_US_50000x_Loop" : "MW_Loop", true);
            });
    }

    public hideMaxWin():gsap.core.Timeline {
        return gsap.timeline()
            .add(gsap.to(this.introDarkening, {alpha: 0, duration: 0.25}))
            .add(() => {
                this.maxWinSpine.state.setEmptyAnimation(0, 0);
                this.maxWinSpine.alpha = 0;
            });
    }
}